/**
 * Document Type Registry
 * 
 * Defines all supported document types for the selling documents upload system.
 * Each document type includes metadata, categorization, and field mappings to
 * TA6, TA7, and LPE1 form fields.
 */

export enum DocumentCategory {
  CORE = 'Core Documents',
  FINANCIAL = 'Financial Documents',
  PLANNING = 'Planning & Building',
  LEASEHOLD = 'Leasehold Specific',
  OPTIONAL = 'Optional but Helpful'
}

export interface DocumentType {
  id: string;
  name: string;
  description: string;
  category: DocumentCategory;
  required: boolean;
  allowMultiple: boolean;
  exampleImageUrl?: string;
  helpText?: string;
  formFieldMappings: string[];
  conditionalDisplay?: {
    tenure?: ('freehold' | 'leasehold')[];
    alterationsMade?: boolean;
  };
}

/**
 * Complete registry of all supported document types
 */
export const DOCUMENT_TYPES: DocumentType[] = [
  // ============================================
  // CORE DOCUMENTS
  // ============================================
  {
    id: 'title-register',
    name: 'Title Register / Title Deeds',
    description: 'Official document showing property ownership and any restrictions',
    category: DocumentCategory.CORE,
    required: true,
    allowMultiple: false,
    formFieldMappings: ['TA6.1.1', 'TA6.1.2', 'TA6.10.1'],
    helpText: 'You can obtain this from the Land Registry if you don\'t have a copy'
  },
  {
    id: 'title-plan',
    name: 'Title Plan',
    description: 'Map showing the property boundaries',
    category: DocumentCategory.CORE,
    required: true,
    allowMultiple: false,
    formFieldMappings: ['TA6.1.1', 'TA6.1.2', 'TA6.1.3'],
    helpText: 'Usually provided with the title register from Land Registry'
  },
  {
    id: 'lease-agreement',
    name: 'Lease Agreement',
    description: 'The lease document for leasehold properties',
    category: DocumentCategory.CORE,
    required: true,
    allowMultiple: false,
    formFieldMappings: ['TA7.1.1', 'TA7.1.2', 'LPE1.1.1', 'LPE1.1.2'],
    conditionalDisplay: {
      tenure: ['leasehold']
    },
    helpText: 'The original lease document showing terms and conditions'
  },
  {
    id: 'epc',
    name: 'Energy Performance Certificate (EPC)',
    description: 'Certificate showing the property\'s energy efficiency rating',
    category: DocumentCategory.CORE,
    required: true,
    allowMultiple: false,
    formFieldMappings: ['TA6.15.1', 'TA6.15.2'],
    helpText: 'Required by law for property sales. Valid for 10 years.'
  },

  // ============================================
  // FINANCIAL DOCUMENTS
  // ============================================
  {
    id: 'service-charge-statements',
    name: 'Service Charge Statements',
    description: 'Statements showing service charge amounts for the last 3 years',
    category: DocumentCategory.FINANCIAL,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['TA7.2.1', 'TA7.2.2', 'TA7.2.3', 'LPE1.2.1', 'LPE1.2.2'],
    conditionalDisplay: {
      tenure: ['leasehold']
    },
    helpText: 'Annual statements from the management company or landlord'
  },
  {
    id: 'ground-rent-statements',
    name: 'Ground Rent Statements',
    description: 'Documents showing ground rent amounts and payment history',
    category: DocumentCategory.FINANCIAL,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['TA7.3.1', 'TA7.3.2', 'TA7.3.3'],
    conditionalDisplay: {
      tenure: ['leasehold']
    },
    helpText: 'Statements or demands for ground rent payments'
  },
  {
    id: 'buildings-insurance',
    name: 'Buildings Insurance Policy',
    description: 'Current buildings insurance policy document',
    category: DocumentCategory.FINANCIAL,
    required: false,
    allowMultiple: false,
    formFieldMappings: ['TA7.4.1', 'TA7.4.2', 'LPE1.3.1'],
    conditionalDisplay: {
      tenure: ['leasehold']
    },
    helpText: 'Policy arranged by management company or landlord'
  },
  {
    id: 'management-accounts',
    name: 'Management Company Accounts',
    description: 'Annual accounts from the management company',
    category: DocumentCategory.FINANCIAL,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['TA7.8.1', 'LPE1.7.1'],
    conditionalDisplay: {
      tenure: ['leasehold']
    },
    helpText: 'Financial statements showing income and expenditure'
  },

  // ============================================
  // PLANNING & BUILDING
  // ============================================
  {
    id: 'planning-permissions',
    name: 'Planning Permissions',
    description: 'Approval documents for any building work or alterations',
    category: DocumentCategory.PLANNING,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['TA6.4.1', 'TA6.4.2', 'TA6.4.3'],
    conditionalDisplay: {
      alterationsMade: true
    },
    helpText: 'Include all planning permissions for extensions, conversions, or major alterations'
  },
  {
    id: 'building-control-certificates',
    name: 'Building Control Certificates',
    description: 'Certificates confirming building work complies with regulations',
    category: DocumentCategory.PLANNING,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['TA6.4.4', 'TA6.4.5'],
    conditionalDisplay: {
      alterationsMade: true
    },
    helpText: 'Completion certificates from Building Control or approved inspector'
  },
  {
    id: 'building-regulations-certificates',
    name: 'Building Regulations Completion Certificates',
    description: 'Final certificates showing building regulations compliance',
    category: DocumentCategory.PLANNING,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['TA6.4.4', 'TA6.4.5'],
    conditionalDisplay: {
      alterationsMade: true
    },
    helpText: 'Final sign-off from Building Control'
  },
  {
    id: 'structural-warranties',
    name: 'Structural Warranties / Guarantees',
    description: 'Warranties for structural work (e.g., NHBC, Zurich)',
    category: DocumentCategory.PLANNING,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['TA6.5.1', 'TA6.5.2'],
    helpText: 'Include warranties for extensions, conversions, or new builds'
  },

  // ============================================
  // LEASEHOLD SPECIFIC
  // ============================================
  {
    id: 'management-company-details',
    name: 'Management Company Details / Correspondence',
    description: 'Contact details and recent correspondence with management company',
    category: DocumentCategory.LEASEHOLD,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['TA7.8.1', 'TA7.8.2', 'LPE1.7.1'],
    conditionalDisplay: {
      tenure: ['leasehold']
    },
    helpText: 'Include company details, contact information, and recent letters'
  },
  {
    id: 'section-20-notices',
    name: 'Section 20 Notices',
    description: 'Notices about major works or long-term agreements',
    category: DocumentCategory.LEASEHOLD,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['LPE1.5.1', 'LPE1.2.4'],
    conditionalDisplay: {
      tenure: ['leasehold']
    },
    helpText: 'Consultation notices for works costing over £250 per leaseholder'
  },
  {
    id: 'lease-variations',
    name: 'Lease Variations',
    description: 'Any amendments or variations to the original lease',
    category: DocumentCategory.LEASEHOLD,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['LPE1.1.2'],
    conditionalDisplay: {
      tenure: ['leasehold']
    },
    helpText: 'Deeds of variation or supplemental leases'
  },
  {
    id: 'share-certificate',
    name: 'Share Certificate (Management Company)',
    description: 'Certificate showing share ownership in management company',
    category: DocumentCategory.LEASEHOLD,
    required: false,
    allowMultiple: false,
    formFieldMappings: ['TA7.8.2'],
    conditionalDisplay: {
      tenure: ['leasehold']
    },
    helpText: 'If the property includes shares in the management company'
  },

  // ============================================
  // OPTIONAL BUT HELPFUL
  // ============================================
  {
    id: 'property-survey',
    name: 'Property Survey',
    description: 'Structural survey or homebuyer report',
    category: DocumentCategory.OPTIONAL,
    required: false,
    allowMultiple: false,
    formFieldMappings: ['TA6.1.1', 'TA6.12.1'],
    helpText: 'Any survey conducted when you purchased the property'
  },
  {
    id: 'drainage-utilities',
    name: 'Drainage / Utilities Information',
    description: 'Documents about drainage systems and utility connections',
    category: DocumentCategory.OPTIONAL,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['TA6.7.1', 'TA6.8.1'],
    helpText: 'Include information about septic tanks, private drainage, or shared utilities'
  },
  {
    id: 'boundary-agreements',
    name: 'Boundary Agreements',
    description: 'Agreements with neighbors about boundaries',
    category: DocumentCategory.OPTIONAL,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['TA6.1.1', 'TA6.1.2'],
    helpText: 'Any written agreements about fence ownership or boundary positions'
  },
  {
    id: 'guarantees-warranties',
    name: 'Guarantees / Warranties (Windows, Boiler, etc.)',
    description: 'Warranties for windows, boiler, damp-proofing, etc.',
    category: DocumentCategory.OPTIONAL,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['TA6.5.1', 'TA6.5.2'],
    helpText: 'Include all guarantees that can be transferred to the buyer'
  },
  {
    id: 'neighbor-correspondence',
    name: 'Correspondence with Neighbors',
    description: 'Letters or agreements with neighboring properties',
    category: DocumentCategory.OPTIONAL,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['TA6.2.1', 'TA6.2.2'],
    helpText: 'Include any relevant correspondence about disputes or agreements'
  },
  {
    id: 'environmental-reports',
    name: 'Environmental Reports',
    description: 'Reports about contamination, radon, or environmental issues',
    category: DocumentCategory.OPTIONAL,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['TA6.12.1', 'TA6.12.2'],
    helpText: 'Include any environmental assessments or contamination reports'
  },
  {
    id: 'flood-risk-assessments',
    name: 'Flood Risk Assessments',
    description: 'Reports or correspondence about flood risk',
    category: DocumentCategory.OPTIONAL,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['TA6.13.1', 'TA6.13.2'],
    helpText: 'Include flood risk reports or insurance correspondence'
  },
  {
    id: 'japanese-knotweed-reports',
    name: 'Japanese Knotweed Reports',
    description: 'Surveys or treatment plans for Japanese knotweed',
    category: DocumentCategory.OPTIONAL,
    required: false,
    allowMultiple: true,
    formFieldMappings: ['TA6.14.1'],
    helpText: 'Include professional surveys and any treatment guarantees'
  }
];

/**
 * Get document types visible for a given property configuration
 */
export function getVisibleDocumentTypes(
  tenure: 'freehold' | 'leasehold',
  alterationsMade: boolean
): DocumentType[] {
  return DOCUMENT_TYPES.filter(docType => {
    // Check tenure condition
    if (docType.conditionalDisplay?.tenure) {
      if (!docType.conditionalDisplay.tenure.includes(tenure)) {
        return false;
      }
    }

    // Check alterations condition
    if (docType.conditionalDisplay?.alterationsMade !== undefined) {
      if (docType.conditionalDisplay.alterationsMade !== alterationsMade) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Calculate completion percentage based on uploaded documents
 */
export function calculateCompletionPercentage(
  visibleDocTypes: DocumentType[],
  uploadedDocTypeIds: Set<string>,
  markedUnavailableIds: Set<string>
): number {
  const requiredDocs = visibleDocTypes.filter(dt => dt.required);
  
  if (requiredDocs.length === 0) {
    return 100;
  }

  const completedCount = requiredDocs.filter(dt => 
    uploadedDocTypeIds.has(dt.id) || markedUnavailableIds.has(dt.id)
  ).length;

  return Math.round((completedCount / requiredDocs.length) * 100);
}

/**
 * Get document types grouped by category
 */
export function getDocumentTypesByCategory(
  documentTypes: DocumentType[]
): Map<DocumentCategory, DocumentType[]> {
  const grouped = new Map<DocumentCategory, DocumentType[]>();

  for (const docType of documentTypes) {
    const existing = grouped.get(docType.category) || [];
    existing.push(docType);
    grouped.set(docType.category, existing);
  }

  return grouped;
}

/**
 * Get a document type by ID
 */
export function getDocumentTypeById(id: string): DocumentType | undefined {
  return DOCUMENT_TYPES.find(dt => dt.id === id);
}

// ============================================
// TRUST CATEGORY MAPPING
// ============================================

/**
 * Trust-focused categories for the selling documents UX
 * These provide a simpler, more reassuring grouping for users
 */
export type TrustCategory = 
  | 'Ownership & title'
  | 'Planning & building'
  | 'Guarantees & certificates';

/**
 * Maps existing DocumentCategory to TrustCategory
 */
export const TRUST_CATEGORY_MAP: Record<DocumentCategory, TrustCategory> = {
  [DocumentCategory.CORE]: 'Ownership & title',
  [DocumentCategory.FINANCIAL]: 'Ownership & title',
  [DocumentCategory.PLANNING]: 'Planning & building',
  [DocumentCategory.LEASEHOLD]: 'Ownership & title',
  [DocumentCategory.OPTIONAL]: 'Guarantees & certificates',
};

/**
 * Example documents for each trust category
 */
export const TRUST_CATEGORY_EXAMPLES: Record<TrustCategory, string[]> = {
  'Ownership & title': ['Title deeds', 'Lease agreement', 'Service charge statements'],
  'Planning & building': ['Planning permissions', 'Building control certificates'],
  'Guarantees & certificates': ['Structural warranties', 'Boiler guarantees', 'Window warranties'],
};

/**
 * Get the trust category for a document type
 */
export function getTrustCategory(docType: DocumentType): TrustCategory {
  return TRUST_CATEGORY_MAP[docType.category];
}

/**
 * Get document types grouped by trust category
 */
export function getDocumentTypesByTrustCategory(
  documentTypes: DocumentType[]
): Map<TrustCategory, DocumentType[]> {
  const grouped = new Map<TrustCategory, DocumentType[]>();

  // Initialize all categories to ensure consistent ordering
  const categoryOrder: TrustCategory[] = [
    'Ownership & title',
    'Planning & building',
    'Guarantees & certificates',
  ];

  for (const category of categoryOrder) {
    grouped.set(category, []);
  }

  for (const docType of documentTypes) {
    const trustCategory = getTrustCategory(docType);
    const existing = grouped.get(trustCategory) || [];
    existing.push(docType);
    grouped.set(trustCategory, existing);
  }

  // Remove empty categories
  for (const [category, docs] of grouped.entries()) {
    if (docs.length === 0) {
      grouped.delete(category);
    }
  }

  return grouped;
}

/**
 * Auto-categorise a file by matching its filename against known patterns.
 * Returns the document type ID if a match is found, or null.
 */
const FILENAME_PATTERNS: [RegExp, string][] = [
  [/title\s*(register|deed)/i, 'title-register'],
  [/title\s*plan/i, 'title-plan'],
  [/lease/i, 'lease-agreement'],
  [/epc|energy\s*performance/i, 'epc'],
  [/service\s*charge/i, 'service-charge-statements'],
  [/ground\s*rent/i, 'ground-rent-statements'],
  [/building[s]?\s*insurance/i, 'buildings-insurance'],
  [/management\s*(company\s*)?account/i, 'management-accounts'],
  [/planning\s*permission/i, 'planning-permissions'],
  [/building\s*control/i, 'building-control-certificates'],
  [/building\s*reg/i, 'building-regulations-certificates'],
  [/structural\s*warrant/i, 'structural-warranties'],
  [/section\s*20/i, 'section-20-notices'],
  [/lease\s*variation/i, 'lease-variations'],
  [/share\s*cert/i, 'share-certificate'],
  [/survey/i, 'property-survey'],
  [/drainage|utilit/i, 'drainage-utilities'],
  [/boundar/i, 'boundary-agreements'],
  [/guarantee|warrant/i, 'guarantees-warranties'],
  [/flood/i, 'flood-risk-assessments'],
  [/knotweed/i, 'japanese-knotweed-reports'],
  [/environment/i, 'environmental-reports'],
  [/neighbou?r/i, 'neighbor-correspondence'],
];

export function guessDocumentType(fileName: string): string | null {
  const lower = fileName.toLowerCase();
  for (const [pattern, typeId] of FILENAME_PATTERNS) {
    if (pattern.test(lower)) return typeId;
  }
  return null;
}

